/**
 * useClientDynamicList Hook
 *
 * Fetches options for a parameter declaring `x-ms-client-dynamic-list`. The
 * request goes straight from the browser to the origin resolved for the picker's
 * client type, carrying the signed-in user's token.
 *
 * A sibling of `useConnectorDynamicList` rather than a branch inside it: that hook
 * gates every fetch on a complete connection reference, keys its cache by
 * `connection.id`, and waits on swagger for managed APIs. A client picker has none
 * of those preconditions.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/auth/useAuth';
import { useAppStore } from '@/stores/appStore';
import { getArmBaseUrl } from '@/services/armConfig';
import {
  buildClientDynamicRequest,
  fetchClientDynamicListOptions,
} from '@/services/clientDynamicListService';
import type { DynamicOption } from '@/services/dynamicDataService';
import type { ClientDynamicListConfig } from '@/types/operationParameter';

const CACHE_TTL_MS = 5 * 60 * 1000;

const NO_OPTIONS: DynamicOption[] = [];

interface CacheEntry {
  options: DynamicOption[];
  expiresAt: number;
}

interface InFlightRequest {
  key: string;
  ticket: symbol;
}

const cache = new Map<string, CacheEntry>();

/** Test-only: drop every cached result. */
export function clearClientDynamicListCache(): void {
  cache.clear();
}

export interface UseClientDynamicListOptions {
  /** Picker configuration, verbatim from the manifest. */
  config: ClientDynamicListConfig | undefined;
  /** Current designer values, keyed by manifest input name, with types preserved. */
  parameterValues: Record<string, unknown>;
  /** Manifest input names of the owning operation. Empty disables the check. */
  manifestInputNames?: ReadonlySet<string>;
  /** Whether to fetch automatically once dependencies are satisfied. */
  autoFetch?: boolean;
}

export interface UseClientDynamicListResult {
  options: DynamicOption[];
  isLoading: boolean;
  error: string | undefined;
  hasLoaded: boolean;
  dependenciesSatisfied: boolean;
  missingDependencies: string[];
  refetch: () => void;
}

export function useClientDynamicList({
  config,
  parameterValues,
  manifestInputNames,
  autoFetch = true,
}: UseClientDynamicListOptions): UseClientDynamicListResult {
  const { getArmToken, account } = useAuth();
  const selectedTenantId = useAppStore((state) => state.selectedTenantId);

  const [options, setOptions] = useState<DynamicOption[]>(NO_OPTIONS);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [hasLoaded, setHasLoaded] = useState(false);

  const plan = useMemo(
    () => (config ? buildClientDynamicRequest(config, parameterValues, manifestInputNames) : undefined),
    [config, parameterValues, manifestInputNames]
  );
  const planSignature = JSON.stringify(plan);
  const configRef = useRef(config);
  const planRef = useRef(plan);
  const getArmTokenRef = useRef(getArmToken);
  configRef.current = config;
  planRef.current = plan;
  getArmTokenRef.current = getArmToken;

  // The cache key carries identity as well as the request: getArmToken mints for the
  // selected tenant, so keying on the account alone would serve one tenant's options
  // in another.
  const fetchKey = useMemo(() => {
    if (!config || plan?.status !== 'ready') return null;
    return [
      config.type,
      getArmBaseUrl(),
      account?.homeAccountId ?? '',
      selectedTenantId ?? '',
      plan.method,
      plan.url,
      plan.body === undefined ? '' : JSON.stringify(plan.body),
      JSON.stringify(config.response),
    ].join('::');
  }, [config, plan, account?.homeAccountId, selectedTenantId]);

  // Always points at the latest key so an in-flight fetch can detect that its own
  // key went stale and discard its result.
  const currentKeyRef = useRef<string | null>(fetchKey);
  currentKeyRef.current = fetchKey;

  const lastFetchedKeyRef = useRef<string | null>(null);
  const inFlightRef = useRef<InFlightRequest | null>(null);

  useEffect(() => {
    if (fetchKey !== lastFetchedKeyRef.current) {
      setOptions(NO_OPTIONS);
      setError(undefined);
      setHasLoaded(false);
    }
  }, [fetchKey]);

  const fetchOptions = useCallback(
    async (opts?: { forceRefresh?: boolean }) => {
      const currentConfig = configRef.current;
      const currentPlan = planRef.current;
      if (!planSignature || !currentConfig || !currentPlan) {
        inFlightRef.current = null;
        setIsLoading(false);
        return;
      }

      if (currentPlan.status === 'error') {
        setError(currentPlan.message);
        setOptions(NO_OPTIONS);
        setHasLoaded(false);
        inFlightRef.current = null;
        setIsLoading(false);
        return;
      }

      if (currentPlan.status === 'waiting') {
        setOptions(NO_OPTIONS);
        setHasLoaded(false);
        inFlightRef.current = null;
        setIsLoading(false);
        return;
      }

      const requestKey = fetchKey;
      if (!requestKey) {
        inFlightRef.current = null;
        setIsLoading(false);
        return;
      }

      if (inFlightRef.current?.key === requestKey) return;

      if (!opts?.forceRefresh) {
        const cached = cache.get(requestKey);
        if (cached && cached.expiresAt > Date.now()) {
          setOptions(cached.options);
          setError(undefined);
          setHasLoaded(true);
          lastFetchedKeyRef.current = requestKey;
          inFlightRef.current = null;
          setIsLoading(false);
          return;
        }
      }

      const ticket = Symbol('clientDynamicListFetch');
      inFlightRef.current = { key: requestKey, ticket };
      const isStale = () =>
        currentKeyRef.current !== requestKey || inFlightRef.current?.ticket !== ticket;
      setIsLoading(true);
      setError(undefined);

      try {
        const token = await getArmTokenRef.current();
        if (isStale()) return;

        if (!token) {
          setError('Could not acquire a token for the signed-in user.');
          setOptions(NO_OPTIONS);
          setHasLoaded(false);
          return;
        }

        const result = await fetchClientDynamicListOptions({
          config: currentConfig,
          plan: currentPlan,
          accessToken: token,
        });
        if (isStale()) return;

        cache.set(requestKey, { options: result, expiresAt: Date.now() + CACHE_TTL_MS });
        setOptions(result);
        setError(undefined);
        setHasLoaded(true);
        lastFetchedKeyRef.current = requestKey;
      } catch (err) {
        if (isStale()) return;
        setError(err instanceof Error ? err.message : 'Failed to load options.');
        setOptions(NO_OPTIONS);
        setHasLoaded(false);
        lastFetchedKeyRef.current = requestKey;
      } finally {
        if (inFlightRef.current?.ticket === ticket) {
          inFlightRef.current = null;
          setIsLoading(false);
        }
      }
    },
    [fetchKey, planSignature]
  );

  useEffect(() => {
    if (!autoFetch) return;
    void fetchOptions();
  }, [autoFetch, fetchOptions]);

  const refetch = useCallback(() => {
    void fetchOptions({ forceRefresh: true });
  }, [fetchOptions]);

  return {
    options,
    isLoading,
    error,
    hasLoaded,
    dependenciesSatisfied: plan?.status === 'ready',
    missingDependencies: plan?.status === 'waiting' ? plan.missing : [],
    refetch,
  };
}
